<template>
    <q-dialog
        backdrop-filter="blur(4px) grayscale(50%)"
        :model-value="modelValue"
        @update:model-value="handleClose"
        persistent>
        <q-card style="min-width: 460px; max-width: 540px;">
            <q-card-section class="row items-center q-pb-none">
                <q-icon name="credit_card" color="primary" size="sm" class="q-mr-sm" />
                <div class="text-h6">{{ savedPmMode ? 'Confirm Per-Package Charge' : 'Payment Required to Send' }}</div>
                <q-space />
                <q-btn icon="close" flat round dense @click="handleClose" :disable="processing" />
            </q-card-section>

            <!-- Saved-PM consent branch: simple confirmation card. No Stripe Elements,
                 no address collection - the customer's saved card is used directly. -->
            <template v-if="savedPmMode">
                <q-card-section>
                    <div class="text-body2 q-mb-md">
                        {{ description || 'Per-package overage charge' }}
                    </div>

                    <div v-if="cardBrand || cardLast4" class="row items-center q-mb-md q-pa-sm" style="background: #f0fdf4; border: 1px solid #86efac; border-radius: 6px;">
                        <q-icon name="credit_card" color="positive" size="20px" class="q-mr-sm" />
                        <div class="col" style="font-size: 13px; color: #1f2937; line-height: 1.4;">
                            <div class="text-weight-medium">Saved payment method</div>
                            <div class="text-caption text-grey-7">
                                {{ formattedCardBrand }}<span v-if="cardLast4"> ending in {{ cardLast4 }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="q-mb-md q-pa-sm" style="background: #f5f7fa; border-radius: 6px;">
                        <div class="row items-center text-body2">
                            <span>Subtotal</span>
                            <q-space />
                            <span>${{ (baseAmount || 0).toFixed(2) }}</span>
                        </div>
                        <div v-if="taxAmount !== null" class="row items-center text-caption text-grey-7">
                            <span>
                                Sales tax<span v-if="taxJurisdiction"> ({{ taxJurisdiction }})</span>
                                <span v-if="taxAmount === 0"> not collected for this jurisdiction</span>
                            </span>
                            <q-space />
                            <span>${{ (taxAmount || 0).toFixed(2) }}</span>
                        </div>
                        <q-separator class="q-my-xs" />
                        <div class="row items-center text-subtitle2">
                            <span>Total today</span>
                            <q-space />
                            <span class="text-primary">${{ (totalAmount || 0).toFixed(2) }} {{ (currency || 'usd').toUpperCase() }}</span>
                        </div>
                    </div>

                    <q-banner v-if="errorMessage" class="bg-red-1 text-red-9 q-mt-md" rounded dense>
                        <template v-slot:avatar>
                            <q-icon name="error" color="negative" />
                        </template>
                        {{ errorMessage }}
                    </q-banner>

                    <div class="text-caption text-grey-7 q-mt-md">
                        Confirm to charge your saved payment method and send the package.
                    </div>
                </q-card-section>

                <q-card-actions align="right" class="q-pa-md">
                    <q-btn flat label="Cancel" @click="handleClose" :disable="processing" />
                    <q-btn
                        color="primary"
                        unelevated
                        icon="lock"
                        :label="processing ? 'Processing…' : `Charge $${(totalAmount || 0).toFixed(2)} & Send`"
                        :loading="processing"
                        @click="confirmSavedPmCharge" />
                </q-card-actions>
            </template>

            <!-- Stripe Elements branch (no saved PM): existing two-phase address+card flow. -->
            <template v-else>
            <q-card-section>
                <div class="text-body2 q-mb-md">
                    {{ description || 'Per-package charge' }}
                </div>

                <!-- Phase 2 summary (address saved) -->
                <div v-if="uiStep === 'card'" class="row items-center q-mb-md q-pa-sm" style="background: #f0fdf4; border: 1px solid #86efac; border-radius: 6px;">
                    <q-icon name="check_circle" color="positive" size="18px" class="q-mr-sm" />
                    <div class="col" style="font-size: 13px; color: #1f2937; line-height: 1.4;">
                        <div class="text-weight-medium">Billing address</div>
                        <div class="text-caption text-grey-7">{{ savedAddressSummary }}</div>
                    </div>
                    <q-btn flat dense no-caps label="Edit" color="primary" size="sm" @click="backToAddress" :disable="processing" />
                </div>

                <!-- Tax breakdown (Phase 2) -->
                <div v-if="uiStep === 'card' && clientSecret" class="q-mb-md q-pa-sm" style="background: #f5f7fa; border-radius: 6px;">
                    <div class="row items-center text-body2">
                        <span>Subtotal</span>
                        <q-space />
                        <span>${{ (baseAmount || 0).toFixed(2) }}</span>
                    </div>
                    <div class="row items-center text-caption text-grey-7">
                        <span>
                            Sales tax<span v-if="taxJurisdiction"> ({{ taxJurisdiction }})</span>
                            <span v-if="taxAmount === 0"> not collected for this jurisdiction</span>
                        </span>
                        <q-space />
                        <span>${{ (taxAmount || 0).toFixed(2) }}</span>
                    </div>
                    <q-separator class="q-my-xs" />
                    <div class="row items-center text-subtitle2">
                        <span>Total today</span>
                        <q-space />
                        <span class="text-primary">${{ (totalAmount || 0).toFixed(2) }} {{ (currency || 'usd').toUpperCase() }}</span>
                    </div>
                </div>

                <!-- Phase 1 header -->
                <div v-if="uiStep === 'address'" class="text-caption text-weight-medium text-grey-8 q-mb-xs" style="text-transform: uppercase; letter-spacing: 0.5px; font-size: 11px;">
                    Step 1 - Billing Address
                </div>

                <!-- Stripe AddressElement - always mounted; hidden in Phase 2. -->
                <div
                    ref="addressMount"
                    v-show="uiStep === 'address'"
                    :style="{ opacity: stripeReady ? 1 : 0, transition: 'opacity 0.3s ease' }"
                    class="q-mb-md"></div>

                <q-inner-loading :showing="!stripeReady && uiStep === 'address'" label="Loading address form…" />

                <!-- Phase 2 header + PaymentElement -->
                <div v-if="uiStep === 'card'" class="text-caption text-weight-medium text-grey-8 q-mb-xs" style="text-transform: uppercase; letter-spacing: 0.5px; font-size: 11px;">
                    Step 2 - Payment Method
                </div>
                <div
                    ref="paymentMount"
                    v-show="uiStep === 'card'"
                    :style="{ opacity: paymentReady ? 1 : 0, transition: 'opacity 0.3s ease' }"></div>

                <q-inner-loading :showing="uiStep === 'card' && (!paymentReady || taxLoading)" label="Preparing payment form…" />

                <q-banner v-if="errorMessage" class="bg-red-1 text-red-9 q-mt-md" rounded dense>
                    <template v-slot:avatar>
                        <q-icon name="error" color="negative" />
                    </template>
                    {{ errorMessage }}
                </q-banner>

                <div class="text-caption text-grey-7 q-mt-md">
                    Your card will be charged once for this send. No payment method will be saved.
                </div>
            </q-card-section>

            <q-card-actions align="right" class="q-pa-md">
                <q-btn flat label="Cancel" @click="handleClose" :disable="processing" />

                <!-- Phase 1 primary action -->
                <q-btn
                    v-if="uiStep === 'address'"
                    color="primary"
                    unelevated
                    icon="check"
                    label="Save billing address"
                    :loading="taxLoading"
                    :disable="!addressComplete || taxLoading"
                    @click="saveBillingAddress" />

                <!-- Phase 2 primary action -->
                <q-btn
                    v-if="uiStep === 'card'"
                    color="primary"
                    unelevated
                    icon="lock"
                    :label="processing ? 'Processing…' : `Pay $${(totalAmount || 0).toFixed(2)} & Send`"
                    :loading="processing"
                    :disable="!paymentReady || processing"
                    @click="confirmPayment" />
            </q-card-actions>
            </template>
        </q-card>
    </q-dialog>
</template>

<script>
import { packages as packagesApi } from '@services/signApi.js';
import { loadStripe } from '@stripe/stripe-js';

/**
 * PerPackagePrepayDialog
 * ----------------------
 * Two-mode confirmation dialog for Free-tier per-package overage charges.
 * Used from both PackagesBuilder.vue and PackageDetails.vue.
 *
 * Mode A - `stripe-elements` (default, when no saved PM on file):
 *   Two-phase Stripe Elements collection. Card is NOT saved.
 *   Phase 1: Stripe AddressElement + "Save billing address" button.
 *            Typing fires NO API calls. Save click → one Tax.Calculation
 *            + PaymentIntent create on the server.
 *   Phase 2: Read-only saved-address summary + tax breakdown +
 *            PaymentElement for card entry. Edit link rewinds to Phase 1.
 *
 * Mode B - `saved-pm` (when the regular send returned 402 OverageConsentRequired):
 *   The customer already has a saved payment method on file. We surface a
 *   simple confirmation card with the charge preview (amount, tax, card
 *   brand+last4) and a single Confirm button. On click, we POST to
 *   `/overage/confirm-and-send` which runs the off-session charge server-side
 *   and proceeds with the send. No Stripe Elements involved here - the saved
 *   PM lives on the Stripe customer, and the user's click is the consent
 *   boundary that prevents silent off-session charging.
 *
 * Mode is driven by the `consentPreview` prop. When the parent passes a
 * preview object (from a 402 response), the dialog renders Mode B; otherwise
 * Mode A.
 */
export default {
    name: 'PerPackagePrepayDialog',

    props: {
        modelValue: { type: Boolean, required: true },
        packageId: { type: String, required: true },
        /**
         * Charge preview from a 402 OverageConsentRequired response. When set,
         * the dialog renders the saved-PM confirmation branch (Mode B) instead
         * of Stripe Elements. Shape:
         *   { amount, taxAmount, totalAmount, taxJurisdiction, currency,
         *     cardBrand, cardLast4, description }
         */
        consentPreview: { type: Object, default: null }
    },

    emits: ['update:modelValue', 'success'],

    data() {
        return {
            uiStep: 'address',      // 'address' | 'card'  (Mode A only)
            stripeReady: false,     // AddressElement mounted + ready
            paymentReady: false,    // PaymentElement mounted + ready
            taxLoading: false,
            processing: false,
            errorMessage: null,

            addressComplete: false,
            savedAddress: null,

            // Populated from create-intent response after Save click (Mode A)
            // OR from the consentPreview prop (Mode B).
            paymentIntentId: null,
            clientSecret: null,
            baseAmount: 0,
            taxAmount: 0,
            totalAmount: 0,
            taxJurisdiction: null,
            currency: 'usd',
            description: '',
            cardBrand: null,
            cardLast4: null
        };
    },

    computed: {
        savedPmMode() {
            return !!this.consentPreview;
        },
        savedAddressSummary() {
            const a = this.savedAddress;
            if (!a) return '';
            const parts = [];
            if (a.line1) parts.push(a.line1);
            const cityState = [a.city, a.state].filter(Boolean).join(' ');
            if (cityState) parts.push(cityState);
            if (a.postalCode) parts.push(a.postalCode);
            if (a.country && a.country !== 'US') parts.push(a.country);
            return parts.join(', ');
        },
        formattedCardBrand() {
            if (!this.cardBrand) return 'Card on file';
            const b = String(this.cardBrand).toLowerCase();
            return b.charAt(0).toUpperCase() + b.slice(1);
        }
    },

    watch: {
        modelValue(newVal) {
            if (newVal) {
                if (this.savedPmMode) {
                    this.openSavedPmDialog();
                } else {
                    this.openDialog();
                }
            } else {
                this.teardown();
            }
        }
    },

    beforeUnmount() {
        this.teardown();
    },

    methods: {
        resetState() {
            this.uiStep = 'address';
            this.stripeReady = false;
            this.paymentReady = false;
            this.taxLoading = false;
            this.processing = false;
            this.errorMessage = null;
            this.addressComplete = false;
            this.savedAddress = null;
            this.paymentIntentId = null;
            this.clientSecret = null;
            this.baseAmount = 0;
            this.taxAmount = 0;
            this.totalAmount = 0;
            this.taxJurisdiction = null;
            this.currency = 'usd';
            this.description = '';
            this.cardBrand = null;
            this.cardLast4 = null;
        },

        openSavedPmDialog() {
            // Mode B: hydrate from the consent preview the parent passed in.
            // The preview comes from the 402 response on the regular /send call,
            // which already loaded Stripe customer + PM details server-side.
            this.resetState();
            const p = this.consentPreview || {};
            this.baseAmount = Number(p.amount ?? 0);
            this.taxAmount = p.taxAmount === null || p.taxAmount === undefined ? null : Number(p.taxAmount);
            this.totalAmount = Number(p.totalAmount ?? p.amount ?? 0);
            this.taxJurisdiction = p.taxJurisdiction || null;
            this.currency = p.currency || 'usd';
            this.cardBrand = p.cardBrand || null;
            this.cardLast4 = p.cardLast4 || null;
            this.description = p.description || 'Per-package overage charge';
        },

        async confirmSavedPmCharge() {
            this.processing = true;
            this.errorMessage = null;
            try {
                const { data } = await packagesApi.confirmOverageAndSend(this.packageId);
                this.$emit('success', data);
                this.$emit('update:modelValue', false);
            } catch (err) {
                console.error('[PrePayDialog] confirm-overage-and-send failed:', err);
                const data = err.response?.data;
                this.errorMessage = data?.message
                    || err.displayMessage
                    || 'Charge failed. Please try again or update your payment method in Billing.';
                this.processing = false;
            }
        },

        async openDialog() {
            this.resetState();
            try {
                // Resolve the platform publishable key from the page config the
                // portal already injects for authenticated pages. This is the
                // same source Billing.vue uses for card-save Stripe init.
                const pubKey = window._pageConfig?.stripePublishableKey;
                if (!pubKey) {
                    throw new Error('Stripe is not configured for this page.');
                }
                if (!this._stripe) {
                    this._stripe = await loadStripe(pubKey);
                }
                if (!this._stripe) throw new Error('Stripe.js failed to load');

                // Phase-1 Elements instance - AddressElement-only.
                // Must declare deferred-mode options (mode/amount/currency) even
                // for address-only usage: Stripe's AddressElement autocomplete
                // only activates when Elements is initialized with a recognised
                // intent mode. Without `mode`, Elements runs in a stripped
                // "standalone" mode where autocomplete is silently disabled and
                // the field falls back to plain manual entry.
                // The amount here is a placeholder - the real PI is created
                // later with the actual tax-inclusive total, and a fresh
                // Elements instance is re-created for Phase 2 (payment).
                this._addressElements = this._stripe.elements({
                    mode: 'payment',
                    amount: 150,
                    currency: 'usd',
                    appearance: { theme: 'stripe' }
                });
                const addressElement = this._addressElements.create('address', {
                    mode: 'billing',
                    autocomplete: { mode: 'automatic' }
                });
                addressElement.on('change', (event) => {
                    this.addressComplete = !!(event && event.complete);
                });
                this._addressElement = addressElement;

                await this.$nextTick();
                if (this.$refs.addressMount) {
                    addressElement.mount(this.$refs.addressMount);
                    addressElement.on('ready', () => { this.stripeReady = true; });
                }
            } catch (err) {
                console.error('[PrePayDialog] open failed:', err);
                this.errorMessage = err.message || 'Could not load payment form.';
            }
        },

        async saveBillingAddress() {
            if (!this._addressElement) return;
            let addrResult;
            try {
                addrResult = await this._addressElement.getValue();
            } catch (err) {
                console.warn('[PrePayDialog] addressElement.getValue failed:', err);
                return;
            }
            if (!addrResult || !addrResult.complete || !addrResult.value || !addrResult.value.address) return;

            const src = addrResult.value.address;
            const addr = {
                country: src.country,
                postalCode: src.postal_code || null,
                state: src.state || null,
                city: src.city || null,
                line1: src.line1 || null
            };

            this.taxLoading = true;
            this.errorMessage = null;

            try {
                const { data } = await packagesApi.createPrePayIntent(this.packageId, {
                    countryCode: addr.country,
                    postalCode: addr.postalCode || '',
                    state: addr.state,
                    city: addr.city,
                    line1: addr.line1
                });

                this.paymentIntentId = data.paymentIntentId;
                this.clientSecret = data.clientSecret;
                this.baseAmount = data.amount;
                this.taxAmount = data.taxAmount || 0;
                this.totalAmount = data.totalAmount;
                this.taxJurisdiction = data.taxJurisdiction;
                this.currency = data.currency || 'usd';
                this.description = data.description || this.description;
                this.savedAddress = addr;

                this.uiStep = 'card';
                await this.$nextTick();
                await this.mountPaymentElement();
            } catch (err) {
                console.error('[PrePayDialog] create-intent failed:', err);
                const data = err.response?.data;
                this.errorMessage = data?.message
                    || err.displayMessage
                    || 'Could not initialize payment. Please check your billing address and try again.';
            } finally {
                this.taxLoading = false;
            }
        },

        async mountPaymentElement() {
            try {
                // Phase-2 Elements instance - bound to the PI's clientSecret.
                // A fresh instance is required after every Edit→Save cycle
                // because Stripe Elements can't be re-bound to a new
                // clientSecret on the same instance.
                if (this._paymentElement) {
                    try { this._paymentElement.unmount(); } catch (e) { /* no-op */ }
                    this._paymentElement = null;
                }
                this._paymentElements = this._stripe.elements({
                    clientSecret: this.clientSecret,
                    appearance: { theme: 'stripe' }
                });
                // Hide billing-address fields (including postal code) on the
                // PaymentElement since we already collected the full address
                // in Phase 1. Pass defaultValues.billingDetails.address so
                // Stripe attaches the collected address to the PaymentMethod
                // on confirm - needed for AVS checks and Stripe Tax registry.
                const savedAddr = this.savedAddress || {};
                const paymentElement = this._paymentElements.create('payment', {
                    layout: 'tabs',
                    fields: {
                        billingDetails: {
                            address: 'never'
                        }
                    },
                    defaultValues: {
                        billingDetails: {
                            address: {
                                country: savedAddr.country || 'US',
                                postal_code: savedAddr.postalCode || '',
                                state: savedAddr.state || '',
                                city: savedAddr.city || '',
                                line1: savedAddr.line1 || ''
                            }
                        }
                    }
                });
                this._paymentElement = paymentElement;

                const mountNode = this.$refs.paymentMount;
                if (!mountNode) throw new Error('Payment element mount node not found');
                paymentElement.mount(mountNode);
                paymentElement.on('ready', () => { this.paymentReady = true; });
            } catch (err) {
                console.error('[PrePayDialog] mount PaymentElement failed:', err);
                this.errorMessage = err.message || 'Could not load card entry form.';
            }
        },

        backToAddress() {
            // Flip back to Phase 1. Tear down the Phase-2 Elements instance
            // (bound to the now-abandoned PI); AddressElement instance from
            // Phase 1 is untouched, so the signer's entered values persist.
            // A subsequent Save creates a fresh PI.
            if (this._paymentElement) {
                try { this._paymentElement.unmount(); } catch (e) { /* no-op */ }
                this._paymentElement = null;
            }
            this._paymentElements = null;
            this.paymentReady = false;
            this.uiStep = 'address';
            this.errorMessage = null;
            this.clientSecret = null;
            this.paymentIntentId = null;
            this.baseAmount = 0;
            this.taxAmount = 0;
            this.totalAmount = 0;
            this.taxJurisdiction = null;
        },

        async confirmPayment() {
            if (!this._stripe || !this._paymentElements || !this.clientSecret) {
                this.errorMessage = 'Payment form is not ready. Please close and try again.';
                return;
            }
            this.processing = true;
            this.errorMessage = null;

            try {
                // confirmPayment with a clientSecret-bound Elements instance
                // handles validation + 3DS internally. (Elements created with
                // clientSecret do NOT require a separate elements.submit().)
                //
                // payment_method_data.billing_details.address is required when
                // the PaymentElement has fields.billingDetails.address set to
                // 'never' - Stripe validates that we pass through whatever
                // billing address data we told Stripe to hide from the user.
                const savedAddr = this.savedAddress || {};
                const { error, paymentIntent } = await this._stripe.confirmPayment({
                    elements: this._paymentElements,
                    clientSecret: this.clientSecret,
                    confirmParams: {
                        return_url: window.location.href,
                        payment_method_data: {
                            billing_details: {
                                address: {
                                    country: savedAddr.country || 'US',
                                    postal_code: savedAddr.postalCode || '',
                                    state: savedAddr.state || '',
                                    city: savedAddr.city || '',
                                    line1: savedAddr.line1 || ''
                                }
                            }
                        }
                    },
                    redirect: 'if_required'
                });

                if (error) {
                    this.errorMessage = error.message || 'Payment was declined. Please try another card.';
                    this.processing = false;
                    return;
                }

                if (!paymentIntent || paymentIntent.status !== 'succeeded') {
                    this.errorMessage = `Payment did not complete (${paymentIntent?.status || 'unknown'}). Please try again.`;
                    this.processing = false;
                    return;
                }

                const { data } = await packagesApi.confirmPrePayAndSend(this.packageId, paymentIntent.id);
                this.$emit('success', data);
                this.$emit('update:modelValue', false);
            } catch (err) {
                console.error('[PrePayDialog] confirm-and-send failed:', err);
                const data = err.response?.data;
                this.errorMessage = data?.message
                    || err.displayMessage
                    || 'Payment was accepted but the package could not be sent. You will not be charged twice - please click Pay & Send again.';
                this.processing = false;
            }
        },

        handleClose(val) {
            // Ignore upstream close events that bump the model-value to
            // something truthy (e.g. a re-open while already open).
            if (val === true) return;

            // Never dismiss mid-charge - confirmPayment is in flight and the
            // server side of the transaction hasn't settled yet. Everything
            // else (Step 1 address entry, Step 2 before clicking Pay) can
            // cancel unconditionally.
            if (this.processing) return;

            this.$emit('update:modelValue', false);
        },

        teardown() {
            this.resetState();
            // Destroy both Elements instances - they're bound to DOM nodes
            // that will be unmounted by the dialog close.
            if (this._addressElement) {
                try { this._addressElement.unmount(); } catch (e) { /* no-op */ }
            }
            if (this._paymentElement) {
                try { this._paymentElement.unmount(); } catch (e) { /* no-op */ }
            }
            this._addressElement = null;
            this._paymentElement = null;
            this._addressElements = null;
            this._paymentElements = null;
            // Keep _stripe cached - it's safe to reuse across opens.
        }
    }
};
</script>
